import React from 'react'
import { Box, Typography } from '@mui/material'
import { Button, Input, RadialBackgroundContainer } from '../../interaxDesignSystemComponents'
import { DefaultModal } from './DefaultModal'
import { INTERAX_COLORS } from '../../constants/colors'

interface EditAccountModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  displayName: string
  phoneNumber: string
  onDisplayNameChange: (value: string) => void
  onPhoneNumberChange: (value: string) => void
  error?: string
  isUpdating: boolean
  colors: {
    textPrimary: string
    textError: string
  }
  translations: {
    title: string
    displayNameLabel: string
    displayNamePlaceholder: string
    phoneNumberLabel: string
    phoneNumberPlaceholder: string
    cancel: string
    save: string
    loading: string
  }
}

/**
 * EditAccountModal - Shared modal component for editing account information
 * Used by both gamer and facilitator profiles
 */
export const EditAccountModal: React.FC<EditAccountModalProps> = ({
  isOpen,
  onClose,
  onSave,
  displayName,
  phoneNumber,
  onDisplayNameChange,
  onPhoneNumberChange,
  error,
  isUpdating,
  colors,
  translations
}) => {
  return (
    <DefaultModal isOpen={isOpen} onClose={onClose} backgroundColor={INTERAX_COLORS.heritageBlue40}>
      <RadialBackgroundContainer useFixedHeight={false}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Modal Title */}
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: colors.textPrimary,
              marginBottom: '8px'
            }}
          >
            {translations.title}
          </Typography>

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box>
              <Typography sx={{ color: colors.textPrimary, fontSize: '14px', marginBottom: '8px' }}>
                {translations.displayNameLabel} <span style={{ color: colors.textError }}>*</span>
              </Typography>
              <Input
                value={displayName}
                onChange={(e) => onDisplayNameChange(e.target.value)}
                placeholder={translations.displayNamePlaceholder}
                fullWidth
                required
                error={!!error && !displayName.trim()}
              />
            </Box>

            <Box>
              <Typography sx={{ color: colors.textPrimary, fontSize: '14px', marginBottom: '8px' }}>
                {translations.phoneNumberLabel}
              </Typography>
              <Input
                value={phoneNumber}
                onChange={(e) => onPhoneNumberChange(e.target.value)}
                placeholder={translations.phoneNumberPlaceholder}
                fullWidth
                type="text"
              />
            </Box>

            {error && <Typography sx={{ color: colors.textError, fontSize: '14px' }}>{error}</Typography>}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={isUpdating}>
              {translations.cancel}
            </Button>
            <Button variant="primary" onClick={onSave} disabled={isUpdating}>
              {isUpdating ? translations.loading : translations.save}
            </Button>
          </Box>
        </Box>
      </RadialBackgroundContainer>
    </DefaultModal>
  )
}
